// src/pages/Admin/AdminSupport.jsx
import React, { useState, useEffect } from 'react';
import { supportAPI } from '../../api/support.js';
import { useToast } from '../../hooks/useToast.js';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { Button } from '../../components/ui/Button/Button.jsx';

export const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const { success, error } = useToast();

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    setIsLoading(true);
    try {
      const response = await supportAPI.getAll({ limit: 50 });
      setTickets(response.data.data || []);
    } catch (err) {
      error('Не удалось загрузить обращения');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponse = async (ticketId) => {
    if (!responseText.trim()) {
      error('Введите текст ответа');
      return;
    }
    try {
      await supportAPI.addResponse(ticketId, { response: responseText });
      success('Ответ отправлен');
      setResponseText('');
      setSelectedTicket(null);
      loadTickets();
    } catch (err) {
      error('Не удалось отправить ответ');
    }
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await supportAPI.update(ticketId, { status });
      success('Статус обновлен');
      loadTickets();
    } catch (err) {
      error('Не удалось обновить статус');
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка обращений..." />;
  }

  return (
    <div>
      <h2 style={{ marginBottom: '24px' }}>Обращения в поддержку</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            style={{
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              borderLeft: `4px solid ${
                ticket.priority === 'urgent' ? '#DC3545' :
                ticket.priority === 'high' ? '#FD7E14' :
                ticket.priority === 'normal' ? '#FFC107' :
                '#28A745'
              }`,
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: 'rgb(var(--c-text))' }}>
                  {ticket.subject}
                </div>
                <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
                  от {ticket.full_name} ({ticket.email})
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '2px 10px',
                  borderRadius: '100px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: ticket.status === 'closed' ? '#E8E8E8' :
                                ticket.status === 'resolved' ? '#D4EDDA' :
                                ticket.status === 'in_progress' ? '#CCE5FF' :
                                '#FFF3CD',
                  color: ticket.status === 'closed' ? '#6B6B6B' :
                         ticket.status === 'resolved' ? '#155724' :
                         ticket.status === 'in_progress' ? '#004085' :
                         '#856404',
                }}>
                  {ticket.status === 'open' ? 'Открыто' :
                   ticket.status === 'in_progress' ? 'В работе' :
                   ticket.status === 'waiting' ? 'Ожидает' :
                   ticket.status === 'resolved' ? 'Решено' :
                   'Закрыто'}
                </span>
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgb(var(--c-border))',
                    fontSize: '12px',
                    outline: 'none',
                  }}
                >
                  <option value="open">Открыто</option>
                  <option value="in_progress">В работе</option>
                  <option value="waiting">Ожидает</option>
                  <option value="resolved">Решено</option>
                  <option value="closed">Закрыто</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '12px', fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
              {ticket.message}
            </div>

            {ticket.response && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'rgb(var(--c-bg))',
                borderRadius: '8px',
                fontSize: '14px',
              }}>
                <strong>Ответ:</strong> {ticket.response}
              </div>
            )}

            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
              >
                {selectedTicket?.id === ticket.id ? 'Закрыть' : 'Ответить'}
              </Button>
              {ticket.status !== 'closed' && (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleStatusChange(ticket.id, 'closed')}
                >
                  Закрыть
                </Button>
              )}
            </div>

            {selectedTicket?.id === ticket.id && (
              <div style={{ marginTop: '16px' }}>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Введите ответ..."
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgb(var(--c-border))',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px',
                    fontFamily: 'inherit',
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => handleResponse(ticket.id)}
                  style={{ marginTop: '8px' }}
                >
                  Отправить ответ
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};